import {
  SystemConfig,
  FinancialParams,
  SolarData,
  EnergyProduction,
  CashFlowYear,
} from '../types';
import { calculateEnergyProduction, calculateFinancialMetrics } from './calculations';

export interface MonteCarloResult {
  npv: number;
  irr: number;
  payback: number;
}

export interface MonteCarloAnalysis {
  iterations: number;
  results: MonteCarloResult[];
  statistics: {
    npv: {
      mean: number;
      median: number;
      min: number;
      max: number;
      p10: number;
      p50: number;
      p90: number;
      stdDev: number;
    };
    irr: {
      mean: number;
      median: number;
      min: number;
      max: number;
      p10: number;
      p50: number;
      p90: number;
      stdDev: number;
    };
  };
}

export interface SensitivityParam {
  name: string;
  baseValue: number;
  min: number;
  max: number;
  step: number;
}

export interface SensitivityResult {
  parameter: string;
  values: number[];
  npvResults: number[];
  irrResults: number[];
}

export interface ScenarioAnalysis {
  conservative: {
    name: string;
    assumptions: Record<string, any>;
    results: {
      npv: number;
      irr: number;
      payback: number;
      totalRevenue: number;
    };
  };
  realistic: {
    name: string;
    assumptions: Record<string, any>;
    results: {
      npv: number;
      irr: number;
      payback: number;
      totalRevenue: number;
    };
  };
  optimistic: {
    name: string;
    assumptions: Record<string, any>;
    results: {
      npv: number;
      irr: number;
      payback: number;
      totalRevenue: number;
    };
  };
}

export interface RiskScore {
  overall: number; // 0-100
  components: {
    location: { score: number; description: string };
    market: { score: number; description: string };
    technical: { score: number; description: string };
    financial: { score: number; description: string };
  };
}

/**
 * Run Monte Carlo simulation
 */
export function runMonteCarloSimulation(
  solarData: SolarData,
  systemConfig: SystemConfig,
  financialParams: FinancialParams,
  iterations: number = 10000
): MonteCarloAnalysis {
  const results: MonteCarloResult[] = [];

  for (let i = 0; i < iterations; i++) {
    // Randomize key parameters with normal distribution
    const randomSolarData = {
      ...solarData,
      annualGHI: normalRandom(solarData.annualGHI, solarData.annualGHI * 0.08), // ±8% variation
      monthlyGHI: solarData.monthlyGHI.map(ghi => normalRandom(ghi, ghi * 0.1)),
    };

    const randomSystemConfig = {
      ...systemConfig,
      inverterEfficiency: normalRandom(systemConfig.inverterEfficiency, 2), // ±2% variation
      losses: {
        ...systemConfig.losses,
        degradation: normalRandom(systemConfig.losses.degradation, 0.2), // ±0.2% variation
      },
    };

    const randomFinancialParams = {
      ...financialParams,
      electricityPrice: normalRandom(financialParams.electricityPrice, financialParams.electricityPrice * 0.15), // ±15% variation
      electricityPriceEscalation: normalRandom(financialParams.electricityPriceEscalation, 1.5), // ±1.5% variation
      opex: {
        ...financialParams.opex,
        maintenance: normalRandom(financialParams.opex.maintenance, financialParams.opex.maintenance * 0.2),
      },
    };

    // Calculate metrics with randomized parameters
    const energyProduction = calculateEnergyProduction(randomSolarData, randomSystemConfig);
    const financialMetrics = calculateFinancialMetrics(energyProduction, randomFinancialParams);

    results.push({
      npv: financialMetrics.npv,
      irr: financialMetrics.irr,
      payback: financialMetrics.paybackPeriod,
    });
  }

  // Calculate statistics
  const npvValues = results.map(r => r.npv).sort((a, b) => a - b);
  const irrValues = results.map(r => r.irr).sort((a, b) => a - b);

  const statistics = {
    npv: calculateStatistics(npvValues),
    irr: calculateStatistics(irrValues),
  };

  return {
    iterations,
    results,
    statistics,
  };
}

/**
 * Perform sensitivity analysis
 */
export function runSensitivityAnalysis(
  solarData: SolarData,
  systemConfig: SystemConfig,
  financialParams: FinancialParams
): SensitivityResult[] {
  const results: SensitivityResult[] = [];

  // Define parameters to analyze
  const parameters: SensitivityParam[] = [
    {
      name: 'Electricity Price',
      baseValue: financialParams.electricityPrice,
      min: financialParams.electricityPrice * 0.5,
      max: financialParams.electricityPrice * 1.5,
      step: financialParams.electricityPrice * 0.1,
    },
    {
      name: 'CAPEX',
      baseValue: Object.values(financialParams.capex).reduce((sum, val) => sum + (val || 0), 0),
      min: Object.values(financialParams.capex).reduce((sum, val) => sum + (val || 0), 0) * 0.7,
      max: Object.values(financialParams.capex).reduce((sum, val) => sum + (val || 0), 0) * 1.3,
      step: Object.values(financialParams.capex).reduce((sum, val) => sum + (val || 0), 0) * 0.1,
    },
    {
      name: 'Panel Degradation',
      baseValue: systemConfig.losses.degradation,
      min: 0.3,
      max: 1.2,
      step: 0.1,
    },
    {
      name: 'Annual OPEX',
      baseValue: Object.values(financialParams.opex).reduce((sum, val) => sum + (val || 0), 0),
      min: Object.values(financialParams.opex).reduce((sum, val) => sum + (val || 0), 0) * 0.5,
      max: Object.values(financialParams.opex).reduce((sum, val) => sum + (val || 0), 0) * 2,
      step: Object.values(financialParams.opex).reduce((sum, val) => sum + (val || 0), 0) * 0.25,
    },
    {
      name: 'Solar Irradiance',
      baseValue: solarData.annualGHI,
      min: solarData.annualGHI * 0.8,
      max: solarData.annualGHI * 1.2,
      step: solarData.annualGHI * 0.05,
    },
  ];

  parameters.forEach(param => {
    const values: number[] = [];
    const npvResults: number[] = [];
    const irrResults: number[] = [];

    for (let value = param.min; value <= param.max; value += param.step) {
      values.push(value);

      // Modify the appropriate parameter
      let modifiedSolarData = { ...solarData };
      let modifiedSystemConfig = { ...systemConfig };
      let modifiedFinancialParams = { ...financialParams };

      if (param.name === 'Electricity Price') {
        modifiedFinancialParams.electricityPrice = value;
      } else if (param.name === 'CAPEX') {
        const scaleFactor = value / param.baseValue;
        modifiedFinancialParams.capex = {
          ...financialParams.capex,
          panelCost: financialParams.capex.panelCost * scaleFactor,
          inverterCost: financialParams.capex.inverterCost * scaleFactor,
          bosCost: financialParams.capex.bosCost * scaleFactor,
          softCosts: financialParams.capex.softCosts * scaleFactor,
        };
      } else if (param.name === 'Panel Degradation') {
        modifiedSystemConfig.losses.degradation = value;
      } else if (param.name === 'Annual OPEX') {
        const scaleFactor = value / param.baseValue;
        modifiedFinancialParams.opex = {
          ...financialParams.opex,
          maintenance: financialParams.opex.maintenance * scaleFactor,
          insurance: financialParams.opex.insurance * scaleFactor,
          monitoring: financialParams.opex.monitoring * scaleFactor,
        };
      } else if (param.name === 'Solar Irradiance') {
        const scaleFactor = value / param.baseValue;
        modifiedSolarData.annualGHI = value;
        modifiedSolarData.monthlyGHI = solarData.monthlyGHI.map(ghi => ghi * scaleFactor);
      }

      // Calculate metrics
      const energyProduction = calculateEnergyProduction(modifiedSolarData, modifiedSystemConfig);
      const financialMetrics = calculateFinancialMetrics(energyProduction, modifiedFinancialParams);

      npvResults.push(financialMetrics.npv);
      irrResults.push(financialMetrics.irr);
    }

    results.push({
      parameter: param.name,
      values,
      npvResults,
      irrResults,
    });
  });

  return results;
}

/**
 * Generate scenario analysis
 */
export function generateScenarioAnalysis(
  solarData: SolarData,
  systemConfig: SystemConfig,
  financialParams: FinancialParams
): ScenarioAnalysis {
  // Conservative scenario
  const conservativeSolarData = {
    ...solarData,
    annualGHI: solarData.annualGHI * 0.92, // -8% solar resource
    monthlyGHI: solarData.monthlyGHI.map(ghi => ghi * 0.92),
  };
  const conservativeSystemConfig = {
    ...systemConfig,
    losses: {
      ...systemConfig.losses,
      degradation: 0.8, // Higher degradation
    },
  };
  const conservativeFinancialParams = {
    ...financialParams,
    electricityPriceEscalation: 1.5, // Lower price growth
    opex: {
      ...financialParams.opex,
      maintenance: financialParams.opex.maintenance * 1.3, // Higher maintenance
    },
  };

  const conservativeEnergy = calculateEnergyProduction(conservativeSolarData, conservativeSystemConfig);
  const conservativeMetrics = calculateFinancialMetrics(conservativeEnergy, conservativeFinancialParams);

  // Realistic scenario (base case)
  const realisticEnergy = calculateEnergyProduction(solarData, systemConfig);
  const realisticMetrics = calculateFinancialMetrics(realisticEnergy, financialParams);

  // Optimistic scenario
  const optimisticSolarData = {
    ...solarData,
    annualGHI: solarData.annualGHI * 1.08, // +8% solar resource
    monthlyGHI: solarData.monthlyGHI.map(ghi => ghi * 1.08),
  };
  const optimisticSystemConfig = {
    ...systemConfig,
    losses: {
      ...systemConfig.losses,
      degradation: 0.35, // Lower degradation
    },
  };
  const optimisticFinancialParams = {
    ...financialParams,
    electricityPriceEscalation: 4.5, // Higher price growth
    opex: {
      ...financialParams.opex,
      maintenance: financialParams.opex.maintenance * 0.8, // Lower maintenance
    },
  };

  const optimisticEnergy = calculateEnergyProduction(optimisticSolarData, optimisticSystemConfig);
  const optimisticMetrics = calculateFinancialMetrics(optimisticEnergy, optimisticFinancialParams);

  return {
    conservative: {
      name: 'Conservative',
      assumptions: {
        solarResource: '-8%',
        degradation: '0.8%/year',
        electricityPriceGrowth: '1.5%/year',
        opexMultiplier: '1.3x',
      },
      results: {
        npv: conservativeMetrics.npv,
        irr: conservativeMetrics.irr,
        payback: conservativeMetrics.paybackPeriod,
        totalRevenue: conservativeMetrics.totalRevenue,
      },
    },
    realistic: {
      name: 'Realistic',
      assumptions: {
        solarResource: 'Base',
        degradation: `${systemConfig.losses.degradation}%/year`,
        electricityPriceGrowth: `${financialParams.electricityPriceEscalation}%/year`,
        opexMultiplier: '1x',
      },
      results: {
        npv: realisticMetrics.npv,
        irr: realisticMetrics.irr,
        payback: realisticMetrics.paybackPeriod,
        totalRevenue: realisticMetrics.totalRevenue,
      },
    },
    optimistic: {
      name: 'Optimistic',
      assumptions: {
        solarResource: '+8%',
        degradation: '0.35%/year',
        electricityPriceGrowth: '4.5%/year',
        opexMultiplier: '0.8x',
      },
      results: {
        npv: optimisticMetrics.npv,
        irr: optimisticMetrics.irr,
        payback: optimisticMetrics.paybackPeriod,
        totalRevenue: optimisticMetrics.totalRevenue,
      },
    },
  };
}

/**
 * Calculate risk score
 */
export function calculateRiskScore(
  solarData: SolarData,
  systemConfig: SystemConfig,
  financialParams: FinancialParams,
  monteCarloResults: MonteCarloAnalysis
): RiskScore {
  // Location risk: Based on solar data variability
  const locationVariability = monteCarloResults.statistics.npv.stdDev / Math.abs(monteCarloResults.statistics.npv.mean);
  const locationScore = Math.max(0, Math.min(100, 100 - locationVariability * 200));
  const locationRisk = locationScore > 70 ? 'Low' : locationScore > 40 ? 'Medium' : 'High';

  // Market risk: Based on electricity price sensitivity
  const priceVolatility = financialParams.electricityPriceEscalation;
  const marketScore = Math.max(0, Math.min(100, 70 - priceVolatility * 5));
  const marketRisk = marketScore > 70 ? 'Low' : marketScore > 40 ? 'Medium' : 'High';

  // Technical risk: Based on panel quality and degradation
  const degradationRisk = systemConfig.losses.degradation;
  const technicalScore = Math.max(0, Math.min(100, 100 - degradationRisk * 50));
  const technicalRisk = technicalScore > 70 ? 'Low' : technicalScore > 40 ? 'Medium' : 'High';

  // Financial risk: Based on investment size and probability of negative NPV
  const negativeNPVCount = monteCarloResults.results.filter(r => r.npv < 0).length;
  const probabilityOfLoss = (negativeNPVCount / monteCarloResults.iterations) * 100;
  const financialScore = Math.max(0, Math.min(100, 100 - probabilityOfLoss * 2));
  const financialRisk = financialScore > 70 ? 'Low' : financialScore > 40 ? 'Medium' : 'High';

  // Overall risk score (weighted average)
  const overall = Math.round((locationScore * 0.3 + marketScore * 0.25 + technicalScore * 0.25 + financialScore * 0.2));

  return {
    overall,
    components: {
      location: {
        score: Math.round(locationScore),
        description: `${locationRisk} risk - Solar resource variability`,
      },
      market: {
        score: Math.round(marketScore),
        description: `${marketRisk} risk - Electricity price volatility`,
      },
      technical: {
        score: Math.round(technicalScore),
        description: `${technicalRisk} risk - Panel degradation and quality`,
      },
      financial: {
        score: Math.round(financialScore),
        description: `${financialRisk} risk - ${probabilityOfLoss.toFixed(1)}% chance of loss`,
      },
    },
  };
}

// Helper functions

/**
 * Generate random number with normal distribution
 */
function normalRandom(mean: number, stdDev: number): number {
  // Box-Muller transform
  const u1 = Math.random();
  const u2 = Math.random();
  const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + z0 * stdDev;
}

/**
 * Calculate statistics from array of values
 */
function calculateStatistics(values: number[]) {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;

  const mean = sorted.reduce((sum, val) => sum + val, 0) / n;
  const median = sorted[Math.floor(n / 2)];
  const min = sorted[0];
  const max = sorted[n - 1];
  const p10 = sorted[Math.floor(n * 0.1)];
  const p50 = median;
  const p90 = sorted[Math.floor(n * 0.9)];

  // Standard deviation
  const variance = sorted.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / n;
  const stdDev = Math.sqrt(variance);

  return { mean, median, min, max, p10, p50, p90, stdDev };
}
